/**
 * Stellar DEX Market Maker for AURG
 * 
 * Mirrors XRPL market maker logic on Stellar.
 * Posts passive offers on Stellar DEX.
 */

import * as StellarSdk from '@stellar/stellar-sdk';
import type { Grams } from './types/Weight';
import { displayGrams } from './types/Weight';

export interface StellarMarketMakerConfig {
  readonly marketMakerPublicKey: string;
  readonly issuerPublicKey: string;
  readonly assetCode: string;
  readonly baseAsset: 'XLM' | 'USDC' | 'USDT';
  readonly baseIssuer?: string;
  readonly spreadBps: number;
  readonly maxOrderSizeAsset: string;
  readonly maxOrderSizeBase: string;
  readonly orderLevels: number;
  readonly levelSpacingBps: number;
}

export interface StellarMarketMakerState {
  readonly assetBalance: string;
  readonly baseBalance: string;
  readonly openBids: number;
  readonly openAsks: number;
  readonly totalBidVolume: string;
  readonly totalAskVolume: string;
  readonly midPrice: number;
}

export interface StellarOffer {
  readonly id: string;
  readonly selling: { code: string; issuer?: string };
  readonly buying: { code: string; issuer?: string };
  readonly amount: string;
  readonly price: string;
  readonly priceR: { n: number; d: number };
}

/**
 * Stellar Market Maker
 * 
 * Posts managed offers on Stellar DEX.
 */
export class StellarMarketMaker {
  private server: StellarSdk.Horizon.Server;
  private network: StellarSdk.Networks;
  private config: StellarMarketMakerConfig;
  
  constructor(
    config: StellarMarketMakerConfig,
    horizonUrl: string = 'https://horizon.stellar.org',
    isTestnet: boolean = false
  ) {
    this.config = config;
    this.server = new StellarSdk.Horizon.Server(horizonUrl);
    this.network = isTestnet ? StellarSdk.Networks.TESTNET : StellarSdk.Networks.PUBLIC;
  }
  
  /**
   * Get current market maker state
   */
  async getState(): Promise<StellarMarketMakerState> {
    const account = await this.server.loadAccount(this.config.marketMakerPublicKey);
    
    // Get asset balance
    const assetBalance = account.balances.find(b => {
      if (b.asset_type === 'native') return false;
      if (b.asset_type === 'liquidity_pool_shares') return false;
      return b.asset_code === this.config.assetCode && b.asset_issuer === this.config.issuerPublicKey;
    });
    
    // Get base balance
    let baseBalance = '0';
    if (this.config.baseAsset === 'XLM') {
      const native = account.balances.find(b => b.asset_type === 'native');
      baseBalance = native ? native.balance : '0';
    } else {
      const base = account.balances.find(b => {
        if (b.asset_type === 'native') return false;
        if (b.asset_type === 'liquidity_pool_shares') return false;
        return b.asset_code === this.config.baseAsset && b.asset_issuer === this.config.baseIssuer;
      });
      baseBalance = base ? (base as any).balance : '0';
    }
    
    // Get open offers
    const offers = await this.getOpenOffers();
    const bids = offers.filter(o => o.buying.code === this.config.assetCode);
    const asks = offers.filter(o => o.selling.code === this.config.assetCode);
    
    const totalBidVolume = bids.reduce((sum, o) => sum + parseFloat(o.amount), 0).toFixed(4);
    const totalAskVolume = asks.reduce((sum, o) => sum + parseFloat(o.amount), 0).toFixed(4);
    
    // Calculate mid-price
    const midPrice = this.calculateMidPrice(offers);
    
    return {
      assetBalance: assetBalance ? (assetBalance as any).balance : '0',
      baseBalance,
      openBids: bids.length,
      openAsks: asks.length,
      totalBidVolume,
      totalAskVolume,
      midPrice
    };
  }
  
  /**
   * Post market making orders
   */
  async postOrders(
    marketMakerKeypair: StellarSdk.Keypair,
    spotPricePerGram: number,
    gramsPerToken: Grams
  ): Promise<string[]> {
    const fairValue = spotPricePerGram * displayGrams(gramsPerToken);
    
    const bidLevels = this.generateBidLevels(fairValue);
    const askLevels = this.generateAskLevels(fairValue);
    
    const account = await this.server.loadAccount(marketMakerKeypair.publicKey());
    const asset = new StellarSdk.Asset(this.config.assetCode, this.config.issuerPublicKey);
    const baseAsset = this.config.baseAsset === 'XLM'
      ? StellarSdk.Asset.native()
      : new StellarSdk.Asset(this.config.baseAsset, this.config.baseIssuer!);
    
    const transactionBuilder = new StellarSdk.TransactionBuilder(account as any, {
      fee: StellarSdk.BASE_FEE,
      networkPassphrase: this.network
    });
    
    // Add bid operations (buy asset, sell base)
    for (const level of bidLevels) {
      transactionBuilder.addOperation(
        StellarSdk.Operation.manageSellOffer({
          selling: baseAsset,
          buying: asset,
          amount: (parseFloat(level.quantity) * level.price).toFixed(7),
          price: (1 / level.price).toFixed(7)
        })
      );
    }
    
    // Add ask operations (sell asset, buy base)
    for (const level of askLevels) {
      transactionBuilder.addOperation(
        StellarSdk.Operation.manageSellOffer({
          selling: asset,
          buying: baseAsset,
          amount: level.quantity,
          price: level.price.toFixed(7)
        })
      );
    }
    
    const transaction = transactionBuilder.setTimeout(30).build();
    transaction.sign(marketMakerKeypair);
    
    const result = await this.server.submitTransaction(transaction);
    return [result.hash];
  }
  
  /**
   * Cancel all open offers
   */
  async cancelAllOffers(marketMakerKeypair: StellarSdk.Keypair): Promise<string> {
    const account = await this.server.loadAccount(marketMakerKeypair.publicKey());
    const offers = await this.getOpenOffers();
    
    const transactionBuilder = new StellarSdk.TransactionBuilder(account as any, {
      fee: StellarSdk.BASE_FEE,
      networkPassphrase: this.network
    });
    
    for (const offer of offers) {
      transactionBuilder.addOperation(
        StellarSdk.Operation.manageSellOffer({
          selling: offer.selling.code === 'XLM' ? StellarSdk.Asset.native() : new StellarSdk.Asset(offer.selling.code, offer.selling.issuer!),
          buying: offer.buying.code === 'XLM' ? StellarSdk.Asset.native() : new StellarSdk.Asset(offer.buying.code, offer.buying.issuer!),
          amount: '0',
          price: '1',
          offerId: offer.id as any
        })
      );
    }
    
    const transaction = transactionBuilder.setTimeout(30).build();
    transaction.sign(marketMakerKeypair);
    
    const result = await this.server.submitTransaction(transaction);
    return result.hash;
  }
  
  /**
   * Generate bid levels
   */
  private generateBidLevels(fairValue: number): Array<{ price: number; quantity: string }> {
    const levels: Array<{ price: number; quantity: string }> = [];
    
    for (let i = 0; i < this.config.orderLevels; i++) {
      const bps = this.config.spreadBps + (i * this.config.levelSpacingBps);
      const price = fairValue * (1 - bps / 10000);
      
      levels.push({
        price,
        quantity: this.config.maxOrderSizeAsset
      });
    }
    
    return levels;
  }
  
  /**
   * Generate ask levels
   */
  private generateAskLevels(fairValue: number): Array<{ price: number; quantity: string }> {
    const levels: Array<{ price: number; quantity: string }> = [];
    
    for (let i = 0; i < this.config.orderLevels; i++) {
      const bps = this.config.spreadBps + (i * this.config.levelSpacingBps);
      const price = fairValue * (1 + bps / 10000);
      
      levels.push({
        price,
        quantity: this.config.maxOrderSizeAsset
      });
    }
    
    return levels;
  }
  
  /**
   * Get open offers
   */
  private async getOpenOffers(): Promise<StellarOffer[]> {
    const offersResponse = await this.server.offers()
      .forAccount(this.config.marketMakerPublicKey)
      .limit(200)
      .call();
    
    return offersResponse.records.map(offer => ({
      id: String(offer.id),
      selling: {
        code: offer.selling.asset_type === 'native' ? 'XLM' : offer.selling.asset_code!,
        issuer: offer.selling.asset_type === 'native' ? undefined : offer.selling.asset_issuer
      },
      buying: {
        code: offer.buying.asset_type === 'native' ? 'XLM' : offer.buying.asset_code!,
        issuer: offer.buying.asset_type === 'native' ? undefined : offer.buying.asset_issuer
      },
      amount: offer.amount,
      price: offer.price,
      priceR: offer.price_r
    }));
  }
  
  /**
   * Calculate mid-price from offers
   */
  private calculateMidPrice(offers: StellarOffer[]): number {
    const bids = offers
      .filter(o => o.buying.code === this.config.assetCode)
      .map(o => 1 / parseFloat(o.price));
    
    const asks = offers
      .filter(o => o.selling.code === this.config.assetCode)
      .map(o => parseFloat(o.price));
    
    if (bids.length === 0 || asks.length === 0) {
      return 0;
    }
    
    const bestBid = Math.max(...bids);
    const bestAsk = Math.min(...asks);
    
    return (bestBid + bestAsk) / 2;
  }
}

/**
 * RECOMMENDED CONFIG for AURG/XLM pair on Stellar
 */
export const AURG_XLM_STELLAR_CONFIG = {
  assetCode: 'AURG',
  baseAsset: 'XLM' as const,
  spreadBps: 50,
  maxOrderSizeAsset: '1000',
  maxOrderSizeBase: '500',
  orderLevels: 5,
  levelSpacingBps: 20
};
